'use client';

import { useTranslations } from 'next-intl';
import { Check, RotateCcw, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { OcrQaPageVerdictDialog } from './ocr-qa-page-verdict-dialog';
import { OCR_QA_HASH, OCR_QA_ACTIVE_STAGES } from '../constants';
import type { OcrQaResult, OcrQaTier, OcrQaVerdict } from '../types';

interface Props {
  tier: OcrQaTier;
  containerId: number;
  result: OcrQaResult;
  /** GLOBAL 1-based page this section covers. */
  pageNumber: number;
  pageLabel: string | null;
}

/**
 * Accept/reject for ONE page, rendered in that page's own section footer.
 *
 * The QA screen offers exactly two scopes: a page, or the whole container.
 * These buttons used to submit a verdict on the whole OcrResult — which covers
 * whatever pages its chunks landed on, sometimes every page in the container —
 * and rendered only on each result's first section, which is why they appeared
 * on a seemingly random handful of pages while each one silently decided the
 * fate of several.
 *
 * The confirm dialog is a URL-hash overlay (`#verdict-page/<resultId>/<page>/<verdict>`).
 * Every page shares the hash key, so an instance only treats the dialog as its
 * own when both the result id and the page match.
 */
export function OcrQaPageVerdictButtons({
  tier,
  containerId,
  result,
  pageNumber,
  pageLabel
}: Props) {
  const t = useTranslations('ocrQa');
  const {
    open: verdictOpen,
    params: verdictParams,
    openWith: openVerdict,
    onOpenChange: closeVerdict
  } = useHashParamDialog(OCR_QA_HASH.verdictPage);

  const [hashResultId, hashPage, hashVerdict] = verdictParams;
  const isMine =
    verdictOpen &&
    hashResultId === String(result.id) &&
    hashPage === String(pageNumber);
  const pendingVerdict = isMine
    ? ((hashVerdict as OcrQaVerdict | undefined) ?? null)
    : null;

  if (!OCR_QA_ACTIVE_STAGES[tier].includes(result.qa_stage ?? '')) {
    return null;
  }

  // Only a verdict from the CURRENT round counts. A resubmitted container
  // carries last round's rows, and treating those as standing left every page
  // already decided with no way to review it again.
  const own = (result.qa_page_reviews ?? []).find(
    (r) => r.page_number === pageNumber && r.tier === tier && !r.superseded_at
  );

  // Already decided at this tier. Re-offering accept AND reject reads as though
  // nothing had happened; the page is settled, so it says so, and changing the
  // call becomes a deliberate second action. There are only two verdicts, so
  // "instead" is unambiguous — it opens the other one. The underlying row is
  // unique per (page, tier), so it overwrites rather than accumulating.
  if (own) {
    const wasAccepted = own.verdict === 'accepted';

    return (
      <>
        <div className='flex items-center gap-2'>
          <Badge
            variant='outline'
            className={
              wasAccepted
                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-600'
                : 'border-red-500/30 bg-red-500/10 text-red-600'
            }
          >
            {wasAccepted ? (
              <Check className='me-1 h-3 w-3' />
            ) : (
              <X className='me-1 h-3 w-3' />
            )}
            {wasAccepted ? t('pageAlreadyAccepted') : t('pageAlreadyRejected')}
          </Badge>
          <Button
            size='sm'
            variant='ghost'
            className='h-7 text-xs'
            onClick={() =>
              openVerdict(
                String(result.id),
                String(pageNumber),
                wasAccepted ? 'rejected' : 'accepted'
              )
            }
          >
            <RotateCcw className='me-1.5 h-3 w-3' />
            {wasAccepted ? t('changeToReject') : t('changeToAccept')}
          </Button>
        </div>

        <OcrQaPageVerdictDialog
          tier={tier}
          containerId={containerId}
          result={result}
          pageNumber={pageNumber}
          pageLabel={pageLabel}
          verdict={pendingVerdict}
          onClose={() => closeVerdict(false)}
        />
      </>
    );
  }

  return (
    <>
      <div className='flex items-center gap-2'>
        <Button
          size='sm'
          variant='destructive'
          onClick={() =>
            openVerdict(String(result.id), String(pageNumber), 'rejected')
          }
        >
          <X className='me-1.5 h-3.5 w-3.5' />
          {t('rejectPage')}
        </Button>
        <Button
          size='sm'
          onClick={() =>
            openVerdict(String(result.id), String(pageNumber), 'accepted')
          }
        >
          <Check className='me-1.5 h-3.5 w-3.5' />
          {t('acceptPage')}
        </Button>
      </div>

      <OcrQaPageVerdictDialog
        tier={tier}
        containerId={containerId}
        result={result}
        pageNumber={pageNumber}
        pageLabel={pageLabel}
        verdict={pendingVerdict}
        onClose={() => closeVerdict(false)}
      />
    </>
  );
}
